import React from 'react';
import styled from 'styled-components';
import * as remoteApi from 'api/remote';
import theme from 'theme';
import { useTranslation } from 'react-i18next';
import { DragHandle, WidgetContainer, WidgetContent, WidgetHeader } from '.';
import { DashboardWidget, Workspace } from 'types';
import KoalaIcon from 'koala/components/Icons';
import { useQuery } from 'react-query';
import queryKeys from 'config/queryKeys';
import { shallowEqual, useSelector } from 'react-redux';
import KoalaLoader from 'koala/components/Loader';
import KoalaConfidencePie from 'koala/components/ConfidencePie';
import { CustomTermKey, translate } from 'utils/customTermUtils';
import KoalaColorBlock from 'koala/components/ColorBlock';
import { VictoryTooltip } from 'victory';

const Container = styled.div`
  height: 18rem;
  display: flex;
  width: 100%;
  height: 90%;
  max-height: 100%;
  gap: ${theme.spacing.x2};
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.x4};
  height: 100%;

  @media ${theme.devices.mobile} {
    max-height: 25rem;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  row-gap: ${theme.spacing.x3};
  column-gap: ${theme.spacing.x3};
  height: 100%;
  justify-content: center;
`;

const LegendItem = styled.div`
  display: flex;
  gap: ${theme.spacing.x1};
`;

interface ProgressData {
  x: number;
  y: number;
  color: string;
  legendTitle?: string;
  label?: string;
}

interface Props {
  widget: DashboardWidget;
}

function InitiativeDueDatePieWidget(props: Props) {
  const { widget } = props;
  const currentWorkspace: Workspace = useSelector((state: any) => state.session.currentWorkspace, shallowEqual);
  const { t } = useTranslation();

  const initiativesFilterParams = {
    archived: false,
    is_in_progress: true,
  };

  const queryKeyInitiativesStats = [
    queryKeys.initiatives,
    'stats',
    {
      workspaceSlug: currentWorkspace.slug,
      filter: initiativesFilterParams,
    },
  ];
  const { data: initiativesStatsData, isLoading } = useQuery(
    queryKeyInitiativesStats,
    remoteApi.fetchInitiativesStats,
    {
      staleTime: 0,
    },
  );

  const widgetTitle: string = widget.title
    ? widget.title
    : t('workspaceDashboards.due-date-pie-chart', {
        initiative: translate(currentWorkspace, CustomTermKey.INITIATIVE, 1),
      });

  const data = initiativesStatsData?.data;

  if (!data) {
    return (
      <WidgetContainer>
        <WidgetHeader>
          <DragHandle className="drag-handle">
            <KoalaIcon iconName="grab" iconSize="small" />
          </DragHandle>
          <p className="widget-type">{widgetTitle}</p>
        </WidgetHeader>
        <WidgetContent>
          <p>{t('workspaceDashboards.widgetError')}</p>
        </WidgetContent>
      </WidgetContainer>
    );
  }

  const progressData: ProgressData[] = [
    {
      x: 0,
      y: data['due_this_week_initiatives_count'],
      color: theme.colors.Y20,
      label: `${data['due_this_week_initiatives_count']} Due this week`,
    },
    {
      x: 1,
      y: data['overdue_initiatives_count'],
      color: theme.colors.R20,
      label: `${data['overdue_initiatives_count']} Overdue`,
    },
    {
      x: 2,
      y: data['completed_on_time_initiatives_count'],
      color: theme.colors.G20,
      label: `${data['completed_on_time_initiatives_count']} Completed on time`,
    },
  ];

  const labelComponent = (
    <VictoryTooltip
      orientation="right"
      flyoutPadding={{ top: 4, bottom: 4, left: 8, right: 8 }}
      cornerRadius={4}
      flyoutStyle={{
        fill: theme.colors.N5,
        stroke: theme.colors.N5,
      }}
      style={[
        {
          fontSize: '12px',
          fontFamily: theme.font.fontFamily,
        },
      ]}
    />
  );

  return (
    <WidgetContainer>
      <WidgetHeader>
        <DragHandle className="drag-handle">
          <KoalaIcon iconName="grab" iconSize="small" />
        </DragHandle>
        <p className="widget-type">{widgetTitle}</p>
      </WidgetHeader>
      <WidgetContent>
        {isLoading && <KoalaLoader />}
        <Container className="panel-checkins-chart">
          <KoalaConfidencePie data={progressData} size="large" tooltipComponent={labelComponent} />
          <LegendContainer>
            {progressData.map((data) => {
              return (
                <LegendItem>
                  <KoalaColorBlock color={data.color} />
                  {data.label}
                </LegendItem>
              );
            })}
          </LegendContainer>
        </Container>
      </WidgetContent>
    </WidgetContainer>
  );
}

export default InitiativeDueDatePieWidget;
